import { AppWindow } from '../../definitions/AppWindow';

declare let window: AppWindow;

import throttle from 'lodash/throttle';
import fptiTrackingProxy from './fptiTrackingProxy';
import {
  closestWithAttribute,
  elementsWithAttribute,
  getFPTIImpression,
  getFPTIProps,
  getImpressionViewport,
  getPSImpression,
  getPSURL,
  isDisplayed,
  isImpressionEnabled,
  isLoadEnabled,
  isInViewport,
  TRACKING,
} from './trackingDomUtils';
import { parseFPTI, sendFPTITracking, sendPSTracking } from './trackingUtils';
import visualDebug, { addStats, consoleLogStats } from './visualDebug';

export const IMPRESSION_THROTTLE_INTERVAL = 1000;
export const VISUAL_TRACKING_PARAM = 'vtracking';
let IMPRESSION_CHECK_EVENTS = {};
let SENT = new Set();
let TOTAL_SENT = 0;
let NODES_PROCESSED = 0;
let VISUAL_TRACKING = false;
let FPTI_PROXY = null;
let ACTIVE_ELEMENT = null;

export function checkImpression(elem, eventName = '') {
  NODES_PROCESSED++;
  if (isDisplayed(elem) && isImpressionEnabled(elem) && isInViewport(elem)) {
    const psImpression = getPSImpression(elem);
    const fptiImpression = getFPTIImpression(elem);
    const reimpressElem = closestWithAttribute(elem, TRACKING.IMPRESSION_REIMPRESS_EVENT);
    const reimpress = Boolean(
      reimpressElem && eventName && eventName === reimpressElem.getAttribute(TRACKING.IMPRESSION_REIMPRESS_EVENT),
    );

    if (psImpression) {
      if (sendPSTracking(psImpression, SENT, reimpress)) {
        markSent(elem, TRACKING.PS_IMPRESSION, psImpression);
      }
    }
    if (fptiImpression) {
      const fptiProps = getFPTIProps(elem, null, parseFPTI(fptiImpression));
      if (sendFPTITracking(FPTI_PROXY, 'im', fptiProps, SENT, reimpress)) {
        markSent(elem, TRACKING.FPTI_IMPRESSION, fptiProps);
      }
    }
  }
}

// NOTE: this function gets called the most and performance optimizations should be done here
export function checkImpressionsInContainer(container, eventName = '') {
  if (!container) {
    return; // container may have been removed
  }
  sendLoadEvents(container);
  const attributeList = [TRACKING.PS_IMPRESSION, TRACKING.FPTI_IMPRESSION];

  const elements = [...elementsWithAttribute(attributeList, container)];
  if (getPSImpression(container) || getFPTIImpression(container)) {
    elements.push(container);
  }
  elements.forEach((elem) => checkImpression(elem, eventName));
}

export function registerImpressionCheckEvents(attrib, container = null) {
  elementsWithAttribute(attrib, container).forEach((elem) => {
    const eventName = elem.getAttribute(attrib) || '';
    let elements = IMPRESSION_CHECK_EVENTS[eventName];
    if (!elements) {
      window.addEventListener(eventName, () => {
        IMPRESSION_CHECK_EVENTS[eventName].forEach((elem) => {
          checkImpressionsInContainer(elem, eventName);
        });
      });
      elements = [];
      IMPRESSION_CHECK_EVENTS[eventName] = elements;
    }
    if (!elements.includes(elem)) {
      elements.push(elem);
    }
  });
}

export function sendLoadEvents(container = null) {
  elementsWithAttribute(TRACKING.PS_PRESENTMENT, container).forEach((elem) => {
    const url = elem.getAttribute(TRACKING.PS_PRESENTMENT);
    if (isLoadEnabled(elem) && sendPSTracking(url, SENT)) {
      markSent(elem, TRACKING.PS_PRESENTMENT, url);
    }
    NODES_PROCESSED++;
  });
  elementsWithAttribute(TRACKING.FPTI_LOAD, container).forEach((elem) => {
    const fptiProps = getFPTIProps(elem, TRACKING.FPTI_LOAD);
    if (isLoadEnabled(elem) && sendFPTITracking(FPTI_PROXY, 'av', fptiProps, SENT)) {
      markSent(elem, TRACKING.FPTI_LOAD, fptiProps);
    }
    NODES_PROCESSED++;
  });
}

export function sendClickEvent(evt) {
  const fptiTrackingTarget = closestWithAttribute(evt.target, TRACKING.FPTI_CLICK);
  NODES_PROCESSED++;
  if (fptiTrackingTarget) {
    const fptiProps = getFPTIProps(fptiTrackingTarget, TRACKING.FPTI_CLICK);
    sendFPTITracking(FPTI_PROXY, 'cl', fptiProps, SENT, true);
    reportAndVisualDebug(fptiTrackingTarget, TRACKING.FPTI_CLICK, fptiProps);
  }
  const sendPSTrackingTarget = closestWithAttribute(evt.target, TRACKING.PS_CLICK);
  if (sendPSTrackingTarget) {
    const url = getPSURL(sendPSTrackingTarget, TRACKING.PS_CLICK);
    sendPSTracking(url, SENT, true);
    reportAndVisualDebug(sendPSTrackingTarget, TRACKING.PS_CLICK, url);
  }
}

export function registerMoreViewportScrollImpressionChecks(container = null) {
  elementsWithAttribute(TRACKING.IMPRESSION_VIEWPORT, container).forEach((elem) => {
    const viewport = getImpressionViewport(elem);
    if (viewport[TRACKING.IMPRESSION_VIEWPORT_INITIALIZED]) {
      return;
    }
    viewport.addEventListener('scroll', window.cwtThrottledImpressionChecker);
    // since listener is on the element itself, we can directly use the element to store this flag
    viewport[TRACKING.IMPRESSION_VIEWPORT_INITIALIZED] = 'true';
  });
}

export function registerViewportScrollImpressionChecks() {
  const throttledImpressionChecker = throttle(() => {
    checkImpressionsInContainer(document.body);
  }, IMPRESSION_THROTTLE_INTERVAL);
  window.addEventListener('scroll', throttledImpressionChecker);
  window.addEventListener('resize', throttledImpressionChecker);

  window.cwtThrottledImpressionChecker = throttledImpressionChecker;
  registerMoreViewportScrollImpressionChecks();
  throttledImpressionChecker();
}

interface OptionsType {
  visualTracking?: boolean;
}

export default function tracking(options = {}) {
  const { visualTracking }: OptionsType = options || {};
  initTrackingData(visualTracking);
  if (VISUAL_TRACKING) {
    document.body.addEventListener('keyup', (evt) => {
      if (evt.key === 't') {
        consoleLogStats(ACTIVE_ELEMENT);
      }
    });

    document.body.addEventListener('mouseover', (evt) => {
      ACTIVE_ELEMENT = evt.target;
    });
  }
  sendLoadEvents(document.body);
  document.body.addEventListener('click', sendClickEvent);
  registerViewportScrollImpressionChecks();
  registerImpressionCheckEvents(TRACKING.IMPRESSION_EVENT);
  registerImpressionCheckEvents(TRACKING.IMPRESSION_REIMPRESS_EVENT);
  FPTI_PROXY.start();
}

export function updateTracking(container) {
  sendLoadEvents(container);
  registerMoreViewportScrollImpressionChecks(container);
  registerImpressionCheckEvents(TRACKING.IMPRESSION_EVENT, container);
  registerImpressionCheckEvents(TRACKING.IMPRESSION_REIMPRESS_EVENT, container);
  window.cwtThrottledImpressionChecker();
}

function reportAndVisualDebug(elem, attribute, data) {
  TOTAL_SENT++;
  if (VISUAL_TRACKING) {
    visualDebug(elem, attribute);
    addStats(elem, attribute, data);
  }
}

function markSent(elem, attribute, data) {
  reportAndVisualDebug(elem, attribute, data);
}

export function initTrackingData(visualTracking = false) {
  IMPRESSION_CHECK_EVENTS = {};
  SENT = new Set();
  TOTAL_SENT = 0;
  NODES_PROCESSED = 0;
  VISUAL_TRACKING = visualTracking;
  FPTI_PROXY = fptiTrackingProxy();
}

export function getStats() {
  return {
    totalSent: TOTAL_SENT,
    nodesProcessed: NODES_PROCESSED,
  };
}
